import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Highlighter, Text, Eraser, MousePointer2, Image as ImageIcon, Type, Scaling, PenLine, Undo2, Redo2, MoreVertical, MoreHorizontal, Square, Circle, ArrowUpRight, Minus, Trash2,
} from 'lucide-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { HexColorPicker } from 'react-colorful';

interface DrawCanvasModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDone: (dataUrl: string, file: File) => void;
  isDarkMode: boolean;
  initialImage?: string | null;
}

type Tool =
  | 'select'
  | 'pencil'
  | 'highlighter'
  | 'eraser'
  | 'line'
  | 'rectangle'
  | 'ellipse'
  | 'arrow'
  | 'text';

type ElementType = 'line' | 'rectangle' | 'ellipse' | 'arrow' | 'text' | 'image';
interface CanvasElementBase {
  id: string;
  type: ElementType;
  x: number; // top-left of bounding box
  y: number;
  w: number;
  h: number;
  rotation: number; // radians
}
interface StrokeProps { color: string; strokeWidth: number; }
interface TextProps { text: string; color: string; fontSize: number; }
interface ImageProps { src: string; img?: HTMLImageElement | null; blend?: 'highlight'; }
type CanvasElement =
  | (CanvasElementBase & { type: 'line' | 'rectangle' | 'ellipse' | 'arrow'; props: StrokeProps })
  | (CanvasElementBase & { type: 'text'; props: TextProps })
  | (CanvasElementBase & { type: 'image'; props: ImageProps });

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
type TransformMode = { kind: 'move' } | { kind: 'resize'; handle: ResizeHandle } | null;

const COLORS = ['#000000', '#ffffff', '#ef4444', '#f59e0b', '#22c55e', '#06b6d4', '#a855f7'];

const toFileFromDataUrl = async (dataUrl: string, name = 'canvas-drawing.png') => {
  // Avoid fetch(data:) because many CSPs block connect-src to data:
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header?.match(/data:([^;]+);base64/i);
  const mime = mimeMatch?.[1] || 'image/png';
  const binary = atob(base64 || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mime });
  return new File([blob], name, { type: mime });
};

export const DrawCanvasModal: React.FC<DrawCanvasModalProps> = ({ isOpen, onClose, onDone, isDarkMode, initialImage }) => {
  const baseCanvasRef = useRef<HTMLCanvasElement>(null);
  const elementsCanvasRef = useRef<HTMLCanvasElement>(null);
  const inkCanvasRef = useRef<HTMLCanvasElement>(null);
  const tempCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [tool, setTool] = useState<Tool>('pencil');
  const [color, setColor] = useState<string>('#000000');
  const [strokeWidth, setStrokeWidth] = useState<number>(5);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  type EditorState = { base: string | null; ink: string | null; elements: CanvasElement[] };
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const lastMidRef = useRef<{ x: number; y: number } | null>(null);
  const strokePtsRef = useRef<{ x: number; y: number }[]>([]);
  const lastTimeRef = useRef<number | null>(null);
  const speedRef = useRef<number>(0);
  const smoothPtRef = useRef<{ x: number; y: number } | null>(null);
  const [history, setHistory] = useState<EditorState[]>([]);
  const [redoStack, setRedoStack] = useState<EditorState[]>([]);
  const [showTextInput, setShowTextInput] = useState<{ x: number; y: number } | null>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const [showWidthSlider, setShowWidthSlider] = useState(false);
  const [showPaletteMore, setShowPaletteMore] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Removed Emoji states
  const paletteBtnRef = useRef<HTMLButtonElement>(null);
  const palettePopRef = useRef<HTMLDivElement>(null);
  const [palettePos, setPalettePos] = useState<{ top: number; left: number } | null>(null);
  // Ref for click-to-deselect logic
  const potentialDeselectRef = useRef(false);

  const [lastPointer, setLastPointer] = useState<{ x: number; y: number }>({ x: 24, y: 24 });
  // Cache for image elements so history (JSON) restore doesn't lose HTMLImageElement
  const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const highlightMaskRef = useRef<HTMLCanvasElement | null>(null);
  // Reusable buffers for highlight composition to reduce GC and allocation overhead
  const unionHelperRef = useRef<HTMLCanvasElement | null>(null);
  const unionBufferRef = useRef<HTMLCanvasElement | null>(null);

  // Elements layer state
  const [elements, setElements] = useState<CanvasElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [transformMode, setTransformMode] = useState<TransformMode>(null);
  const [transformOrigin, setTransformOrigin] = useState<{ x: number; y: number } | null>(null);
  const [hoverCursor, setHoverCursor] = useState<string>('default');
  const suppressHighlightUnionRef = useRef<boolean>(false);

  const bgClass = isDarkMode ? 'bg-black/90' : 'bg-black/70';
  const panelClass = isDarkMode ? 'bg-[#1f1f1f] text-white border-white/10' : 'bg-white text-gray-900 border-gray-200';

  // helpers to capture and restore full editor state
  const captureState = useCallback(async (): Promise<EditorState> => {
    const base = baseCanvasRef.current;
    const ink = inkCanvasRef.current;
    const baseUrl = base ? base.toDataURL('image/png') : null;
    const inkUrl = ink ? ink.toDataURL('image/png') : null;
    // Deep clone elements via JSON for history snapshot
    const elementsCopy: CanvasElement[] = JSON.parse(JSON.stringify(elements));
    return { base: baseUrl, ink: inkUrl, elements: elementsCopy };
  }, [elements]);

  const drawDataUrl = (canvas: HTMLCanvasElement, dataUrl: string | null) => {
    if (!canvas || !dataUrl) return;
    const img = new Image();
    img.onload = () => {
      const dpr = window.devicePixelRatio || 1;
      const ctx = canvas.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width / dpr, canvas.height / dpr);
    };
    img.src = dataUrl;
  };

  const restoreState = useCallback((state: EditorState) => {
    const base = baseCanvasRef.current;
    const ink = inkCanvasRef.current;
    if (base) drawDataUrl(base, state.base);
    if (ink) drawDataUrl(ink, state.ink);
    setElements(state.elements || []);
    setSelectedId(null);
    setTransformMode(null);
  }, []);

  // Resize canvas to container
  const fitCanvas = useCallback(() => {
    const container = containerRef.current;
    const base = baseCanvasRef.current;
    const temp = tempCanvasRef.current;
    const elems = elementsCanvasRef.current;
    const ink = inkCanvasRef.current;
    if (!container || !base || !temp || !elems || !ink) return;

    // Prevent resize if text input is active (fixes mobile keyboard closing issue)
    if (showTextInput) return;

    const rect = container.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    [base, temp, elems, ink].forEach((c) => {
      c.width = Math.floor(rect.width * dpr);
      c.height = Math.floor(rect.height * dpr);
      c.style.width = rect.width + 'px';
      c.style.height = rect.height + 'px';
      const ctx = c.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
      if (ctx) {
        // Reset any prior transforms to avoid cumulative scaling which causes offset
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
      }
    });

    // Redraw last history snapshot after resize to avoid clearing (restore full state)
    if (history.length > 0) {
      const last = history[history.length - 1];
      // Use a small timeout so canvases have correct size before drawing
      setTimeout(() => restoreState(last), 0);
    } else if (initialImage) {
      // If no history and initial image exists, draw it preserving aspect ratio (contain)
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const ctx = base.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, base.width, base.height);
        const cw = base.width / dpr;
        const ch = base.height / dpr;
        const iw = img.width;
        const ih = img.height;
        const scale = Math.min(cw / iw, ch / ih);
        const w = iw * scale;
        const h = ih * scale;
        const x = (cw - w) / 2;
        const y = (ch - h) / 2;
        ctx.drawImage(img, x, y, w, h);
        // push to history as base state
        pushHistory();
        renderElements();
      };
      img.src = initialImage;
    } else {
      pushHistory();
      renderElements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialImage]);

  useEffect(() => {
    if (!isOpen) return;
    fitCanvas();
    const onResize = () => fitCanvas();
    window.addEventListener('resize', onResize);
    // Observe container size changes for responsive fit
    const ro = new ResizeObserver(() => fitCanvas());
    if (containerRef.current) ro.observe(containerRef.current);
    return () => {
      window.removeEventListener('resize', onResize);
      ro.disconnect();
    };
  }, [isOpen, fitCanvas]);

  // Lock background scroll when modal open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  // Clear eraser cursor when tool changes away from eraser
  useEffect(() => {
    const temp = tempCanvasRef.current;
    if (!temp) return;
    const tctx = temp.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (!tctx) return;
    if (tool !== 'eraser') {
      tctx.clearRect(0, 0, temp.width, temp.height);
    }
  }, [tool, strokeWidth]);



  // Position palette popover after open
  useEffect(() => {
    if (!showPaletteMore) { setPalettePos(null); return; }
    const timer = setTimeout(() => {
      const btn = paletteBtnRef.current;
      const pop = palettePopRef.current;
      if (!btn || !pop) return;
      const br = btn.getBoundingClientRect();
      const pr = pop.getBoundingClientRect();
      const margin = 8;
      let left = br.left + br.width / 2 - pr.width / 2;
      // Always place below the trigger
      let top = Math.min(br.bottom + margin, window.innerHeight - pr.height - margin);
      left = Math.max(margin, Math.min(left, window.innerWidth - pr.width - margin));
      setPalettePos({ top, left });
    }, 0);
    return () => clearTimeout(timer);
  }, [showPaletteMore]);



  const getCtx = (which: 'base' | 'temp') => (which === 'base' ? baseCanvasRef.current : tempCanvasRef.current)?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;

  const getElementsCtx = () => elementsCanvasRef.current?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
  const getInkCtx = () => inkCanvasRef.current?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;

  // Re-add: Compute and draw a dashed outline around the union of all highlight strokes
  const drawHighlightOutline = (
    ctx: CanvasRenderingContext2D,
    targetCanvas: HTMLCanvasElement,
    includeInk: boolean,
  ) => {
    const union = composeHighlightsUnion(targetCanvas.width, targetCanvas.height, includeInk);
    if (!union) return;
    // Extract boundary by scanning alpha
    const uctx = union.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (!uctx) return;
    const dpr = window.devicePixelRatio || 1;
    const img = uctx.getImageData(0, 0, union.width, union.height);
    const data = img.data;
    const w = union.width; const h = union.height;
    const isFilled = (x: number, y: number) => {
      if (x < 0 || y < 0 || x >= w || y >= h) return false;
      return data[(y * w + x) * 4 + 3] !== 0;
    };
    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = isDarkMode ? '#ffffff' : '#ffffff';
    // draw with identity transform so device pixels align
    const prev = ctx.getTransform();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.beginPath();
    for (let y = 0; y < h; y++) {
      let x = 0;
      while (x < w) {
        while (x < w) {
          const f = isFilled(x, y);
          const b = f && (!isFilled(x, y - 1) || !isFilled(x, y + 1) || !isFilled(x - 1, y) || !isFilled(x + 1, y));
          if (b) break; x++;
        }
        if (x >= w) break;
        const start = x;
        while (x < w) {
          const f = isFilled(x, y);
          const b = f && (!isFilled(x, y - 1) || !isFilled(x, y + 1) || !isFilled(x - 1, y) || !isFilled(x + 1, y));
          if (!b) break; x++;
        }
        const end = x;
        ctx.moveTo(start, y);
        ctx.lineTo(end, y);
      }
    }
    ctx.stroke();
    // restore prior transform
    ctx.setTransform(prev);
    ctx.restore();
  };

  // Compose all highlight sources (highlight elements + optional ink) into a single bitmap
  // using max-alpha per pixel to avoid darkening when they overlap.
  const composeHighlightsUnion = (
    widthPx: number,
    heightPx: number,
    includeInk: boolean
  ): HTMLCanvasElement | null => {
    // 1. Collect Valid Sources
    const sources: { canvas: HTMLCanvasElement; x: number; y: number; w: number; h: number }[] = [];
    const dprCanvas = window.devicePixelRatio || 1;

    // Helper to ensure we have a valid source bitmap
    // This duplicates some logic from drawElement to ensure highlight elements from history (undone/redone)
    // are restored from their src URL if the HTMLImageElement was lost in serialization.
    const getSource = (el: CanvasElement): HTMLImageElement | HTMLCanvasElement | null => {
      if (el.type !== 'image') return null;
      let buf = (el.props as any).buf;
      let img = (el.props as any).img;

      // Validate types - JSON.parse might convert them to plain objects e.g. {}
      const isBufValid = buf instanceof HTMLCanvasElement;
      const isImgValid = img instanceof HTMLImageElement;

      if (isBufValid) return buf;
      if (isImgValid) return img;

      // If missing/invalid, try restoration from src
      const src = (el.props as any).src;
      if (src) {
        const cached = imageCacheRef.current.get(src);
        if (cached) {
          (el.props as any).img = cached;
          return cached;
        } else {
          // Trigger load if not already doing so (simple check: if we are here, we need it)
          // We can't return it this frame, but we kick off the load.
          // To avoid spamming loads, we check if we already have a specialized marker? 
          // For now, new Image() is cheap enough if we check cache first.
          // But beware of creating 60 images per second.
          // We'll rely on the fact that if it's not in cache, we start it, and next time it might be there?
          // No, onload is async. 
          // We will use a dedicated "loading" Set in a real app, but here we'll just check if we just requested it.
          // Actually, 'drawElement' handles the load-and-set-state loop. 
          // Since composeHighlightsUnion runs often, we should be careful.
          // Safe path: Just return null. 'drawElement' isn't called for highlights, 
          // so we MUST trigger load here or somewhere.
          // Let's modify the element to mark it as loading to avoid loop.
          if (!(el as any).__isLoading) {
            (el as any).__isLoading = true;
            const newImg = new Image();
            newImg.crossOrigin = 'anonymous';
            newImg.onload = () => {
              imageCacheRef.current.set(src, newImg);
              (el.props as any).img = newImg;
              delete (el as any).__isLoading;
              renderElements(); // Re-render when loaded
            };
            newImg.onerror = () => { delete (el as any).__isLoading; };
            newImg.src = src;
          }
          return null;
        }
      }
      return null;
    };

    // Reuse or create helper canvas for drawing individual sources
    let helper = unionHelperRef.current;
    if (!helper) { helper = document.createElement('canvas'); unionHelperRef.current = helper; }
    if (helper.width !== widthPx || helper.height !== heightPx) { helper.width = widthPx; helper.height = heightPx; }

    // Process Elements
    for (const el of elements) {
      if (el.type !== 'image') continue;
      if ((el.props as any).blend !== 'highlight') continue;

      const srcBitmap = getSource(el);
      if (!srcBitmap) continue;

      // Draw this single element to helper to get its pixels (expensive but needed for max-alpha logic)
      // Optimization: We could just push the bitmap and coords to list and batch process?
      // But we need them scaled and positioned on the full canvas.

      // Actually, we need to draw each source to a full-size canvas to composite them?
      // Or we can draw them directly to the 'helper' canvas, get image data, and merge?
      // The issue is we need to merge MULTIPLE sources.
      // Current logic: source -> canvas -> getImageData -> merge.
      // Optimized logic:
      // Clear helper.
      // Draw source to helper.
      // Get ImageData.
      // Merge into Accumulator.
      // 
      // To avoid clearing/allocating helper for EACH source, we can just use it.
      // But we need separate pixel data for "Source" vs "Accumulator".

      const px = Math.round(el.x * dprCanvas);
      const py = Math.round(el.y * dprCanvas);
      const pw = Math.round(el.w * dprCanvas);
      const ph = Math.round(el.h * dprCanvas);

      // Simple bounds check
      if (px + pw < 0 || py + ph < 0 || px > widthPx || py > heightPx) continue;

      const hctx = helper.getContext('2d', { willReadFrequently: true });
      if (!hctx) continue;

      // We need to clear only the area we are about to draw to avoid full clear overhead?
      // Or just clear full. fast enough likely.
      hctx.clearRect(0, 0, widthPx, heightPx);
      hctx.drawImage(srcBitmap, px, py, pw, ph);

      // Push the Helper canvas as a source? No, we need to extract data NOW before we reuse helper.
      // Wait, if we use one helper, we can't push it to array.
      // PROPOSAL: accumulate immediately.
      // We need an Accumulator Buffer (Result).
    }

    // Reuse or create Accumulator Buffer
    let buffer = unionBufferRef.current;
    if (!buffer) { buffer = document.createElement('canvas'); unionBufferRef.current = buffer; }
    if (buffer.width !== widthPx || buffer.height !== heightPx) { buffer.width = widthPx; buffer.height = heightPx; }

    const bctx = buffer.getContext('2d', { willReadFrequently: true });
    if (!bctx) return null;

    // We should clear buffer before starting accumulation?
    // The accumulator needs to start empty.
    bctx.clearRect(0, 0, widthPx, heightPx);

    // Get Accumulator ImageData (initially empty)
    const outImg = bctx.getImageData(0, 0, widthPx, heightPx);
    const outData32 = new Uint32Array(outImg.data.buffer);

    let hasContent = false;

    // Helper Context
    const hctx = helper.getContext('2d', { willReadFrequently: true });
    if (!hctx) return null;

    // 1. Process Highlight Elements
    for (const el of elements) {
      if (el.type !== 'image') continue;
      if ((el.props as any).blend !== 'highlight') continue;
      const srcBitmap = getSource(el);
      if (!srcBitmap) continue; // Skip weak/invalid images

      const px = Math.round(el.x * dprCanvas);
      const py = Math.round(el.y * dprCanvas);
      const pw = Math.round(el.w * dprCanvas);
      const ph = Math.round(el.h * dprCanvas);

      // 2. Draw source to helper just in the bounding box
      // Optimize: Only clear the area we will draw (since we assume helper is clean from previous loop or we clean after)
      // Actually we clean after usage to be safe, or clean before.
      // Let's clean ONLY the rect we are about to use.
      hctx.clearRect(px, py, pw, ph);
      hctx.drawImage(srcBitmap, px, py, pw, ph);

      // 3. Get ImageData only for the bounding box
      const itemImg = hctx.getImageData(px, py, pw, ph);
      const itemData32 = new Uint32Array(itemImg.data.buffer);

      // 4. Max-Alpha Merge Loop (using Uint32 views for speed)
      // We iterate the small cropped buffer and map to the large output buffer
      for (let y = 0; y < ph; y++) {
        const outRowStart = (py + y) * widthPx + px;
        const itemRowStart = y * pw;
        for (let x = 0; x < pw; x++) {
          const s = itemData32[itemRowStart + x];
          if (s === 0) continue; // Transparent

          const outIdx = outRowStart + x;
          const o = outData32[outIdx];

          if (o === 0) {
            outData32[outIdx] = s;
          } else {
            const sa = s >>> 24;
            const oa = o >>> 24;
            if (sa > oa) outData32[outIdx] = s;
          }
        }
      }

      // Cleanup helper for next iteration? 
      // We rely on clearing the Rect before drawing in next loop.
      // But if next loop draws a DIFFERENT rect, the old dirty rect remains.
      // So we MUST clear what we drew.
      hctx.clearRect(px, py, pw, ph);

      hasContent = true;
    }

    // 2. Process Ink (Highlighter being drawn right now)
    if (includeInk && inkCanvasRef.current) {
      const ink = inkCanvasRef.current;
      // Ink is usually full screen, but we could optimize if we knew bounds.
      // For now, full screen for active stroke.
      hctx.clearRect(0, 0, widthPx, heightPx);
      hctx.drawImage(ink, 0, 0);
      const itemImg = hctx.getImageData(0, 0, widthPx, heightPx);
      const itemData32 = new Uint32Array(itemImg.data.buffer);

      for (let i = 0; i < outData32.length; i++) {
        const s = itemData32[i];
        if (s === 0) continue;
        const o = outData32[i];
        if (o === 0) {
          outData32[i] = s;
        } else {
          const sa = s >>> 24;
          const oa = o >>> 24;
          if (sa > oa) outData32[i] = s;
        }
      }
      // Clear helper
      hctx.clearRect(0, 0, widthPx, heightPx);
      hasContent = true;
    }

    if (!hasContent) return null;

    // Boost alpha (Apply boost to the merged result)
    // 1.75 boost.
    // We can do this on the 32-bit array or 8-bit.
    // 32-bit is tricky to modify component-wise. Back to 8-bit wrapper.
    const outData8 = outImg.data;
    const boost = 1.75;
    for (let i = 3; i < outData8.length; i += 4) {
      const a = outData8[i];
      if (a > 0) {
        // Boost
        outData8[i] = Math.min(255, Math.round(a * boost));
      }
    }

    bctx.putImageData(outImg, 0, 0);
    return buffer;
  };

  const renderElements = useCallback(() => {
    const c = elementsCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    // draw elements
    // draw non-highlight elements first
    elements.forEach((el) => {
      if (el.type === 'image' && (el.props as any).blend === 'highlight') return;
      drawElement(ctx, el);
    });
    // Highlight rendering
    if (suppressHighlightUnionRef.current) {
      // While erasing, draw highlight elements directly so user sees live updates without heavy union recompute
      elements.forEach((el) => {
        if (el.type === 'image' && (el.props as any).blend === 'highlight') {
          drawElement(ctx, el);
        }
      });
    } else {
      const union = composeHighlightsUnion(c.width, c.height, tool === 'highlighter');
      if (union) {
        ctx.save();
        const prev = ctx.getTransform();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = 'source-over';
        ctx.drawImage(union, 0, 0);
        ctx.setTransform(prev);
        ctx.restore();
        drawHighlightOutline(ctx, c, tool === 'highlighter');
      }
    }
    if (tool === 'select' && selectedId) {
      const el = elements.find(e => e.id === selectedId);
      if (el) drawSelectionOutline(ctx, el);
    }
  }, [elements, selectedId, tool]);

  // Draw a cursor ring for brush tools (eraser, pencil, highlighter) on the temp canvas
  const drawEraserCursor = (x: number, y: number) => {
    const temp = tempCanvasRef.current;
    if (!temp) return;
    const tctx = temp.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (!tctx) return;
    tctx.clearRect(0, 0, temp.width, temp.height);
    // Show the same circular cursor for pencil, highlighter, and eraser
    tctx.save();
    const r = Math.max(2, (tool === 'highlighter' ? 40 : strokeWidth) / 2);
    // Soft, blurred drop shadow for the cursor
    tctx.save();
    tctx.shadowColor = 'rgba(63, 0, 0, 0)';
    tctx.shadowBlur = Math.max(6, r * 0.9);
    tctx.shadowOffsetX = 0;
    tctx.shadowOffsetY = Math.max(2, r * 0.35);
    tctx.beginPath();
    tctx.arc(x, y, Math.max(1, r * 0.8), 0, Math.PI * 2);
    // Slight fill so shadow renders without a heavy body
    tctx.fillStyle = 'rgba(0,0,0,0.1)';
    tctx.fill();
    tctx.restore();
    // Fill: translucent white
    tctx.beginPath();
    tctx.arc(x, y, r, 0, Math.PI * 2);
    tctx.fillStyle = 'rgba(255,255,255,0.76)';
    tctx.fill();
    // Ring: slightly darker/stronger white
    tctx.beginPath();
    tctx.arc(x, y, r, 0, Math.PI * 2);
    tctx.strokeStyle = 'rgba(164, 164, 164, 0.9)';
    tctx.lineWidth = 1.3;
    tctx.stroke();
    tctx.restore();
  };

  // Container-level pointer events handled inline in JSX to ensure scope resolution

  // Utility: compute non-transparent pixel bounds of a canvas
  const getContentBounds = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (!ctx) return null;
    const { width, height } = canvas;
    const imgData = ctx.getImageData(0, 0, width, height);
    const data = imgData.data;
    let minX = width, minY = height, maxX = -1, maxY = -1;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const a = data[i + 3];
        if (a !== 0) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX === -1) return null; // empty
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  };

  useEffect(() => { renderElements(); }, [elements, selectedId, renderElements]);

  const drawElement = (ctx: CanvasRenderingContext2D, el: CanvasElement) => {
    ctx.save();
    const cx = el.x + el.w / 2;
    const cy = el.y + el.h / 2;
    ctx.translate(cx, cy);
    ctx.rotate(el.rotation);
    ctx.translate(-cx, -cy);
    if (el.type === 'rectangle') {
      ctx.lineWidth = el.props.strokeWidth;
      ctx.strokeStyle = el.props.color;
      ctx.strokeRect(el.x, el.y, el.w, el.h);
    } else if (el.type === 'ellipse') {
      ctx.beginPath();
      ctx.lineWidth = el.props.strokeWidth;
      ctx.strokeStyle = el.props.color;
      ctx.ellipse(el.x + el.w / 2, el.y + el.h / 2, Math.abs(el.w / 2), Math.abs(el.h / 2), 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (el.type === 'line' || el.type === 'arrow') {
      ctx.beginPath();
      ctx.lineWidth = el.props.strokeWidth;
      ctx.strokeStyle = el.props.color;
      ctx.moveTo(el.x, el.y);
      ctx.lineTo(el.x + el.w, el.y + el.h);
      ctx.stroke();
      if (el.type === 'arrow') {
        const dx = el.w; const dy = el.h;
        const x2 = el.x + el.w; const y2 = el.y + el.h;
        const angle = Math.atan2(dy, dx);
        const len = Math.hypot(dx, dy);
        const head = Math.min(12 + el.props.strokeWidth, len / 3);
        ctx.beginPath();
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
        ctx.moveTo(x2, y2);
        ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
        ctx.stroke();
      }
    } else if (el.type === 'text') {
      ctx.fillStyle = el.props.color;
      ctx.textBaseline = 'top';
      ctx.font = `${el.props.fontSize}px ui-sans-serif, system-ui`;
      ctx.fillText(el.props.text, el.x, el.y);
    } else if (el.type === 'image') {
      const buf = (el.props as any).buf as HTMLCanvasElement | undefined;
      let img = el.props.img as HTMLImageElement | null | undefined;
      if (buf instanceof HTMLCanvasElement) {
        if (el.props.blend === 'highlight') {
          const isElementsLayer = (ctx.canvas === elementsCanvasRef.current);
          ctx.globalCompositeOperation = isElementsLayer ? 'destination-over' : 'source-over';
          ctx.globalAlpha = 1;
        }
        ctx.drawImage(buf, el.x, el.y, el.w, el.h);
      } else {
        // If img missing (e.g., after undo/redo JSON clone), rebuild from src with cache
        if (!(img instanceof HTMLImageElement)) {
          const src = el.props.src;
          if (src) {
            const cache = imageCacheRef.current;
            const cached = cache.get(src);
            if (cached) {
              img = cached;
              (el.props as any).img = cached;
            } else {
              const newImg = new Image();
              newImg.crossOrigin = 'anonymous';
              newImg.onload = () => {
                cache.set(src, newImg);
                (el.props as any).img = newImg;
                requestAnimationFrame(() => renderElements());
              };
              newImg.onerror = () => { };
              newImg.src = src;
              img = undefined;
            }
          }
        }
        if (img instanceof HTMLImageElement) {
          if (el.props.blend === 'highlight') {
            const isElementsLayer = (ctx.canvas === elementsCanvasRef.current);
            ctx.globalCompositeOperation = isElementsLayer ? 'destination-over' : 'source-over';
            ctx.globalAlpha = 1;
          }
          ctx.drawImage(img, el.x, el.y, el.w, el.h);
        }
      }
    }
    ctx.restore();
  };

  const drawSelectionOutline = (ctx: CanvasRenderingContext2D, el: CanvasElement) => {
    ctx.save();
    const cx = el.x + el.w / 2;
    const cy = el.y + el.h / 2;
    ctx.translate(cx, cy);
    ctx.rotate(el.rotation);
    ctx.translate(-cx, -cy);
    ctx.strokeStyle = '#10b981';
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.strokeRect(el.x, el.y, el.w, el.h);
    ctx.setLineDash([]);
    // handles
    const handles: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
    handles.forEach(h => {
      const { hx, hy } = handlePosition(el, h);
      ctx.fillStyle = '#10b981';
      ctx.fillRect(hx - 4, hy - 4, 8, 8);
    });
    // Rotation handle removed
    ctx.restore();
  };

  const handlePosition = (el: CanvasElement, handle: ResizeHandle): { hx: number; hy: number } => {
    const points = {
      nw: { x: el.x, y: el.y },
      n: { x: el.x + el.w / 2, y: el.y },
      ne: { x: el.x + el.w, y: el.y },
      e: { x: el.x + el.w, y: el.y + el.h / 2 },
      se: { x: el.x + el.w, y: el.y + el.h },
      s: { x: el.x + el.w / 2, y: el.y + el.h },
      sw: { x: el.x, y: el.y + el.h },
      w: { x: el.x, y: el.y + el.h / 2 },
    } as const;
    const p = points[handle];
    // apply rotation
    const cx = el.x + el.w / 2; const cy = el.y + el.h / 2;
    const cos = Math.cos(el.rotation); const sin = Math.sin(el.rotation);
    const dx = p.x - cx; const dy = p.y - cy;
    const rx = cx + dx * cos - dy * sin;
    const ry = cy + dx * sin + dy * cos;
    return { hx: rx, hy: ry };
  };

  // Geometry utility: rectangle-circle intersection,
  // used for eraser hit-testing against image elements
  const rectIntersectsCircle = (
    rx: number, ry: number, rw: number, rh: number,
    cx: number, cy: number, cr: number
  ) => {
    const closestX = Math.max(rx, Math.min(cx, rx + rw));
    const closestY = Math.max(ry, Math.min(cy, ry + rh));
    const dx = cx - closestX;
    const dy = cy - closestY;
    return (dx * dx + dy * dy) <= cr * cr;
  };

  const pushHistory = async () => {
    const snapshot = await captureState();
    setHistory((prev) => [...prev, snapshot]);
    setRedoStack([]);
  };

  const beginPath = (x: number, y: number) => {
    const ctx = getInkCtx();
    if (!ctx) return;
    // Clear transient ink layer at the start of each stroke EXCEPT for highlighter,
    // so overlapping highlights don't stack darker across strokes
    const ink = inkCanvasRef.current;
    if (ink && tool !== 'highlighter') ctx.clearRect(0, 0, ink.width, ink.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash([]);

    if (tool === 'highlighter') {
      // Highlighter: use offscreen buffer for the current stroke to avoid self-overlap darkening
      // Init mask if needed
      let off = highlightMaskRef.current;
      const ink = inkCanvasRef.current;
      if (ink && (!off || off.width !== ink.width || off.height !== ink.height)) {
        off = document.createElement('canvas');
        off.width = ink.width;
        off.height = ink.height;
        highlightMaskRef.current = off;
      }
      // Clear the mask for the NEW stroke
      const octx = off?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
      if (octx && off) octx.clearRect(0, 0, off.width, off.height);

      // We will draw onto the mask with standard source-over, then draw the mask to ink with reduced alpha
      ctx.globalAlpha = 1; // Unused for highlighter in this path, but good practice reset
      ctx.globalCompositeOperation = 'source-over';
    } else if (tool === 'eraser') {
      // Avoid highlight union recompute while actively erasing
      suppressHighlightUnionRef.current = true;
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }

    ctx.strokeStyle = tool === 'highlighter' ? '#3b82f6' : color;
    ctx.lineWidth = tool === 'highlighter' ? 40 : strokeWidth;
    ctx.beginPath();
    ctx.moveTo(x, y);
    // initialize smoothing refs
    lastPointRef.current = { x, y };
    lastMidRef.current = { x, y };
    strokePtsRef.current = [{ x, y }];
    lastTimeRef.current = performance.now();
    speedRef.current = 0;
    smoothPtRef.current = { x, y };
  };

  const drawTo = (x: number, y: number) => {
    const ink = inkCanvasRef.current;
    const ictx = getInkCtx();
    if (!startPoint) return;

    if (tool === 'pencil' || tool === 'highlighter' || tool === 'eraser') {
      if (!ink || !ictx) return;
      const pts = strokePtsRef.current;
      const now = performance.now();
      const prevTime = lastTimeRef.current ?? now;
      const dt = Math.max(1, now - prevTime); // ms
      lastTimeRef.current = now;

      // Exponential smoothing on raw input to reduce jitter
      const prevSmooth = smoothPtRef.current ?? { x, y };
      const alpha = 0.5; // Increased from 0.4 for faster response
      const sx = prevSmooth.x + (x - prevSmooth.x) * alpha;
      const sy = prevSmooth.y + (y - prevSmooth.y) * alpha;
      smoothPtRef.current = { x: sx, y: sy };

      const last = pts[pts.length - 1] || { x: sx, y: sy };
      const dxAll = sx - last.x;
      const dyAll = sy - last.y;
      const dist = Math.hypot(dxAll, dyAll);

      // Velocity in px/ms (smoothed)
      const v = dist / dt;
      // Smooth speed to avoid jitter
      const prevV = speedRef.current;
      const sv = prevV + (v - prevV) * 0.35;
      speedRef.current = sv;

      // Adaptive sampling: denser for highlighter to avoid gaps
      const baseSpacing = tool === 'highlighter'
        ? Math.max(0.16, 40 * 0.10)
        : Math.max(0.28, strokeWidth * 0.18);
      const fastFactor = 1 / Math.max(0.35, Math.min(2.2, sv * 1.8)); // widen smoothing window
      const spacing = Math.max(0.18, baseSpacing * fastFactor); // consistent spacing for all tools
      const steps = Math.max(1, Math.floor(dist / spacing));
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const ix = last.x + dxAll * t;
        const iy = last.y + dyAll * t;
        pts.push({ x: ix, y: iy });
      }

      // Draw branch
      // Setup context: Highlighter draws to offscreen mask, others draw to ink canvas
      let targetCtx = ictx;
      let isHighlighter = tool === 'highlighter';
      const effectiveWidth = isHighlighter ? 40 : strokeWidth;

      if (isHighlighter) {
        const off = highlightMaskRef.current;
        if (off) {
          const octx = off.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
          if (octx) targetCtx = octx;
        }
      }

      const prev = lastPointRef.current ?? { x, y };
      // Use smaller max segment length for big widths to avoid dotted look
      const maxSeg = Math.min(2.2, Math.max(0.6, effectiveWidth * 0.16));
      const jumpDist = Math.hypot(x - prev.x, y - prev.y);
      const segs = Math.max(1, Math.ceil(jumpDist / maxSeg));
      let px = prev.x;
      let py = prev.y;

      // Common drawing props
      targetCtx.lineCap = 'round';
      targetCtx.lineJoin = 'round';

      if (isHighlighter) {
        targetCtx.globalAlpha = 1; // Solid on mask
        targetCtx.globalCompositeOperation = 'source-over';
        targetCtx.strokeStyle = color; // Draw with color on mask (we'll tint/alpha it when blitting)
        targetCtx.lineWidth = effectiveWidth; // Fixed highlighter width
      } else if (tool === 'eraser') {
        targetCtx.strokeStyle = 'rgba(0,0,0,1)';
        targetCtx.lineWidth = Math.max(0.5, effectiveWidth * (1.05 - 0.3 * Math.max(0, Math.min(1, sv / 1.5))));
        targetCtx.globalAlpha = 1;
        targetCtx.globalCompositeOperation = 'destination-out';
      } else {
        targetCtx.strokeStyle = color;
        targetCtx.lineWidth = Math.max(0.5, effectiveWidth * (1.05 - 0.3 * Math.max(0, Math.min(1, sv / 1.5))));
        targetCtx.globalAlpha = 1;
        targetCtx.globalCompositeOperation = 'source-over';
      }

      for (let i = 1; i <= segs; i++) {
        const t = i / segs;
        const cx = prev.x + (x - prev.x) * t;
        const cy = prev.y + (y - prev.y) * t;
        const midPrev = lastMidRef.current ?? prev;
        const mid = { x: (px + cx) / 2, y: (py + cy) / 2 };

        targetCtx.beginPath();
        targetCtx.moveTo(midPrev.x, midPrev.y);
        targetCtx.quadraticCurveTo(px, py, mid.x, mid.y);
        targetCtx.stroke();

        // Extra step for eraser: poke holes in image elements
        if (tool === 'eraser') {
          const radius = Math.max(6, strokeWidth) / 2;
          const p = { x: mid.x, y: mid.y };
          let needsRerender = false;
          for (const el of elements) {
            if (el.type !== 'image') continue;
            if (!rectIntersectsCircle(el.x, el.y, el.w, el.h, p.x, p.y, radius)) continue;
            const relX = p.x - el.x;
            const relY = p.y - el.y;
            let buf = (el.props as any).buf as HTMLCanvasElement | undefined;
            if (!(buf instanceof HTMLCanvasElement)) {
              const srcImg = (el.props as any).img as HTMLImageElement | undefined;
              if (!srcImg) continue;
              buf = document.createElement('canvas');
              buf.width = Math.max(1, Math.round(el.w));
              buf.height = Math.max(1, Math.round(el.h));
              const bctx = buf.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
              if (!bctx) continue;
              bctx.drawImage(srcImg, 0, 0, buf.width, buf.height);
              (el.props as any).buf = buf;
            }
            const octx = buf.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
            if (!octx) continue;
            octx.save();
            octx.globalCompositeOperation = 'destination-out';
            octx.beginPath();
            octx.arc(relX, relY, radius, 0, Math.PI * 2);
            octx.fill();
            octx.restore();
            needsRerender = true;
          }
          if (needsRerender) {
            requestAnimationFrame(() => renderElements());
          }
        }

        lastMidRef.current = mid;
        px = cx; py = cy;
      }

      // If highlighter, blit mask to ink canvas (ONCE per frame, after loop)
      // Highlighter blit moved to onPointerMove batch

      // Track points for potential later use (e.g., stroke promotion bounds)
      pts.push({ x, y });
      lastPointRef.current = { x, y };
      lastMidRef.current = { x, y };
      setStartPoint({ x, y });
      // Keep circular cursor visible for all brush tools while drawing
      drawEraserCursor(x, y);
      return;
    }

    const temp = tempCanvasRef.current;
    const tctx = getCtx('temp');
    if (!temp || !tctx) return;

    // Clear temp and render preview for shapes
    tctx.clearRect(0, 0, temp.width, temp.height);
    tctx.save();
    tctx.strokeStyle = color;
    tctx.lineWidth = strokeWidth;
    tctx.globalAlpha = 1;
    tctx.globalCompositeOperation = 'source-over';

    const dx = x - startPoint.x;
    const dy = y - startPoint.y;
    if (tool === 'line') {
      tctx.beginPath();
      tctx.moveTo(startPoint.x, startPoint.y);
      tctx.lineTo(x, y);
      tctx.stroke();
    } else if (tool === 'rectangle') {
      tctx.strokeRect(startPoint.x, startPoint.y, dx, dy);
    } else if (tool === 'ellipse') {
      tctx.beginPath();
      tctx.ellipse(startPoint.x + dx / 2, startPoint.y + dy / 2, Math.abs(dx / 2), Math.abs(dy / 2), 0, 0, Math.PI * 2);
      tctx.stroke();
    } else if (tool === 'arrow') {
      const angle = Math.atan2(dy, dx);
      const len = Math.hypot(dx, dy);
      const head = Math.min(12 + strokeWidth, len / 3);
      // line
      tctx.beginPath();
      tctx.moveTo(startPoint.x, startPoint.y);
      tctx.lineTo(x, y);
      tctx.stroke();
      // arrow head
      tctx.beginPath();
      tctx.moveTo(x, y);
      tctx.lineTo(x - head * Math.cos(angle - Math.PI / 6), y - head * Math.sin(angle - Math.PI / 6));
      tctx.moveTo(x, y);
      tctx.lineTo(x - head * Math.cos(angle + Math.PI / 6), y - head * Math.sin(angle + Math.PI / 6));
      tctx.stroke();
    }

    tctx.restore();
  };

  const commitTempToBase = () => {
    // No-op now that we draw directly on ink canvas for brushes
    const t = tempCanvasRef.current?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    const temp = tempCanvasRef.current;
    if (t && temp) t.clearRect(0, 0, temp.width, temp.height);
  };

  const createElementFromDrag = (endX: number, endY: number) => {
    if (!startPoint) return;
    const sx = startPoint.x; const sy = startPoint.y;
    const dx = endX - sx; const dy = endY - sy;
    const id = Math.random().toString(36).slice(2);
    let el: CanvasElement | null = null;
    if (tool === 'rectangle' || tool === 'ellipse' || tool === 'line' || tool === 'arrow') {
      const bounds = tool === 'line' || tool === 'arrow'
        ? { x: sx, y: sy, w: dx, h: dy }
        : { x: Math.min(sx, endX), y: Math.min(sy, endY), w: Math.abs(dx), h: Math.abs(dy) };
      el = {
        id,
        type: tool as any,
        x: bounds.x,
        y: bounds.y,
        w: bounds.w,
        h: bounds.h,
        rotation: 0,
        props: { color, strokeWidth }
      } as CanvasElement;
    }
    if (el) {
      setElements((prev) => [...prev, el!]);
    }
    // clear temp preview
    const temp = tempCanvasRef.current;
    const tctx = temp?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (temp && tctx) tctx.clearRect(0, 0, temp.width, temp.height);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isOpen) return;
    if (tool === 'select' && e.currentTarget === baseCanvasRef.current) return;
    const rect = containerRef.current?.getBoundingClientRect() as DOMRect;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setLastPointer({ x, y });
    drawEraserCursor(x, y);
    // Selection tool interactions happen on elements canvas
    if (tool === 'select') {
      const hit = hitTest(x, y, elements);
      if (hit) {
        setSelectedId(hit.id);
        const hitHandle = hitTestHandle(x, y, hit);
        if (hitHandle) {
          setTransformMode({ kind: 'resize', handle: hitHandle });
          setTransformOrigin({ x, y });
          (e.currentTarget as any).setPointerCapture?.(e.pointerId);
          setHoverCursor('grabbing');
        } else {
          setTransformMode({ kind: 'move' });
          setTransformOrigin({ x, y });
          (e.currentTarget as any).setPointerCapture?.(e.pointerId);
          setHoverCursor('grabbing');
        }
      } else {
        // Background click
        if (selectedId) {
          // Allow drag-anywhere to move selected element
          setTransformMode({ kind: 'move' });
          setTransformOrigin({ x, y });
          (e.currentTarget as any).setPointerCapture?.(e.pointerId);
          setHoverCursor('grabbing');
          potentialDeselectRef.current = true;
        } else {
          setSelectedId(null);
          setTransformMode(null);
          setTransformOrigin(null);
        }
      }
      return;
    }
    setIsDrawing(true);
    setStartPoint({ x, y });
    if (tool === 'text') {
      setShowTextInput({ x, y });
      setIsDrawing(false);
      setStartPoint(null);
      setTimeout(() => textInputRef.current?.focus(), 0);
      return;
    }
    // Ensure we continue to receive move events even if pointer leaves canvas
    try { (e.currentTarget as any).setPointerCapture?.(e.pointerId); } catch { }
    beginPath(x, y);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (tool === 'select' && (e.currentTarget as HTMLCanvasElement) === baseCanvasRef.current) return;
    const rect = containerRef.current?.getBoundingClientRect() as DOMRect;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const isBrushTool = tool === 'pencil' || tool === 'highlighter' || tool === 'eraser';
    let coalescedPoints: { x: number; y: number }[] | null = null;
    if (isBrushTool) {
      const nativeEvt = e.nativeEvent;
      if (typeof nativeEvt.getCoalescedEvents === 'function') {
        const samples = nativeEvt.getCoalescedEvents();
        if (samples && samples.length > 0) {
          coalescedPoints = samples.map((sample) => ({
            x: sample.clientX - rect.left,
            y: sample.clientY - rect.top,
          }));
          // Ensure the dispatched event is also included so the cursor and stroke stay synced.
          coalescedPoints.push({ x, y });
        }
      }
    }

    const lastCoords = isBrushTool ? (coalescedPoints?.[coalescedPoints.length - 1] ?? { x, y }) : { x, y };
    setLastPointer(lastCoords);
    if (tool === 'select' && selectedId && transformMode && transformOrigin) {
      const dx = x - transformOrigin.x;
      const dy = y - transformOrigin.y;

      // If we are moving and potentialDeselect is true, check if we moved enough to cancel deselect
      if (transformMode.kind === 'move' && potentialDeselectRef.current) {
        if (Math.hypot(dx, dy) > 3) {
          potentialDeselectRef.current = false;
        }
      }

      setElements((prev) => prev.map(el => {
        if (el.id !== selectedId) return el;
        if (transformMode.kind === 'move') {
          return { ...el, x: el.x + dx, y: el.y + dy };
        }
        if (transformMode.kind === 'resize') {
          return resizeElement(el, transformMode.handle, dx, dy);
        }
        return el;
      }));
      // update transformOrigin after applying the delta
      setTransformOrigin({ x, y });
      return;
    }
    // Cursor update when hovering in select mode and not transforming
    if (tool === 'select' && selectedId && !transformMode) {
      const el = elements.find(e2 => e2.id === selectedId);
      if (el) {
        const h = hitTestHandle(x, y, el);
        if (h === 'n' || h === 's') setHoverCursor('ns-resize');
        else if (h === 'e' || h === 'w') setHoverCursor('ew-resize');
        else if (h === 'ne' || h === 'sw') setHoverCursor('nesw-resize');
        else if (h === 'nw' || h === 'se') setHoverCursor('nwse-resize');
        else if (pointInElement(x, y, el)) setHoverCursor('move');
        else setHoverCursor('default');
      } else {
        const hit = hitTest(x, y, elements);
        setHoverCursor(hit ? 'move' : 'default');
      }
    } else if (tool === 'select' && !selectedId) {
      const hit = hitTest(x, y, elements);
      setHoverCursor(hit ? 'move' : 'default');
    } else {
      setHoverCursor('default');
    }
    // Draw cursor ring for brush tools even when not drawing
    if (tool === 'pencil' || tool === 'highlighter' || tool === 'eraser') {
      drawEraserCursor(lastCoords.x, lastCoords.y);
    } else {
      const temp = tempCanvasRef.current;
      const tctx = temp?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
      if (temp && tctx) tctx.clearRect(0, 0, temp.width, temp.height);
    }
    if (!isDrawing || !startPoint) return;
    if (isBrushTool) {
      const points = coalescedPoints ?? [{ x, y }];
      points.forEach((pt) => drawTo(pt.x, pt.y));
      // Optimize: Only blit highlighter mask to screen ONCE per event batch
      if (tool === 'highlighter' && highlightMaskRef.current && inkCanvasRef.current) {
        const ink = inkCanvasRef.current;
        const ictx = getInkCtx();
        if (ictx) {
          ictx.clearRect(0, 0, ink.width, ink.height);
          ictx.save();
          ictx.globalAlpha = 0.28;
          ictx.globalCompositeOperation = 'source-over';
          ictx.drawImage(highlightMaskRef.current, 0, 0);
          ictx.restore();
        }
      }
      return;
    }
    drawTo(x, y);
  };

  const onPointerUp = (e?: React.PointerEvent<HTMLCanvasElement>) => {
    if (tool === 'select') {
      setTransformMode(null);
      setTransformOrigin(null);
      if (e) {
        try { (e.currentTarget as any).releasePointerCapture?.(e.pointerId); } catch { }
      }
      setHoverCursor('default');

      if (potentialDeselectRef.current) {
        setSelectedId(null);
        potentialDeselectRef.current = false;
      }

      // When ending a transform (move/resize), snapshot state
      pushHistory();
      return;
    }
    if (!isDrawing) {
      // Hide eraser cursor ring if present
      const temp = tempCanvasRef.current;
      const tctx = temp?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
      if (temp && tctx) tctx.clearRect(0, 0, temp.width, temp.height);
      return;
    }
    if (tool === 'pencil' || tool === 'highlighter' || tool === 'eraser') {
      // Convert the transient ink canvas (current stroke) into a cropped image element
      const base = baseCanvasRef.current;
      const ink = inkCanvasRef.current;
      if (base && ink) {
        const dpr = window.devicePixelRatio || 1;
        const cw = base.width / dpr;
        const ch = base.height / dpr;
        if (tool !== 'eraser') {
          // For drawing/highlighter: promote stroke to element
          const bounds = getContentBounds(ink);
          if (bounds) {
            const crop = document.createElement('canvas');
            crop.width = bounds.w;
            crop.height = bounds.h;
            const cx = crop.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
            const ix = ink.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
            if (cx && ix) {
              const imgData = ix.getImageData(bounds.x, bounds.y, bounds.w, bounds.h);
              cx.putImageData(imgData, 0, 0);
              const dataUrl = crop.toDataURL('image/png');
              const img = new Image();
              img.onload = () => {
                const id = Math.random().toString(36).slice(2);
                const x = bounds.x / dpr;
                const y = bounds.y / dpr;
                const w = bounds.w / dpr;
                const h = bounds.h / dpr;
                const el: CanvasElement = {
                  id,
                  type: 'image',
                  x,
                  y,
                  w,
                  h,
                  rotation: 0,
                  props: { src: dataUrl, img, ...(tool === 'highlighter' ? { blend: 'highlight' as const } : {}) }
                } as any;
                setElements((prev) => [...prev, el]);
                const ictx = ink.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
                if (ictx) ictx.clearRect(0, 0, ink.width, ink.height);
                pushHistory();
              };
              img.src = dataUrl;
            }
          }
        } else {
          // Eraser: bake buffered edits into images so history persists them, then clear ink layer
          for (const el of elements) {
            if (el.type !== 'image') continue;
            const buf = (el.props as any).buf as HTMLCanvasElement | undefined;
            if (!(buf instanceof HTMLCanvasElement)) continue;
            const dataUrl = buf.toDataURL('image/png');
            const newImg = new Image();
            newImg.onload = () => {
              (el.props as any).img = newImg;
              (el.props as any).src = dataUrl;
              delete (el.props as any).buf;
              requestAnimationFrame(() => renderElements());
            };
            newImg.src = dataUrl;
          }
          const ictx = ink.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
          if (ictx) ictx.clearRect(0, 0, ink.width, ink.height);
          pushHistory();
        }
      }
      // reset smoothing refs after stroke end
      lastPointRef.current = null;
      lastMidRef.current = null;
      strokePtsRef.current = [];
      // Re-enable highlight union drawing after erasing completes
      suppressHighlightUnionRef.current = false;
      // Ensure a final render with union recomputed
      requestAnimationFrame(() => renderElements());
      setIsDrawing(false);
      setStartPoint(null);
      // Release pointer capture if held
      if (e) { try { (e.currentTarget as any).releasePointerCapture?.(e.pointerId); } catch { } }
      return;
    }
    // Create element from shape preview
    if (startPoint) {
      createElementFromDrag(lastPointer.x, lastPointer.y);
    }
    setIsDrawing(false);
    setStartPoint(null);
    // Snapshot after shape creation
    pushHistory();
  };

  const handleTextCommit = () => {
    if (!showTextInput) return;
    const text = textInputRef.current?.value || '';
    if (!text.trim()) {
      setShowTextInput(null);
      return;
    }
    const id = Math.random().toString(36).slice(2);
    const fontSize = Math.max(14, strokeWidth * 4);
    const el: CanvasElement = {
      id,
      type: 'text',
      x: showTextInput.x,
      y: showTextInput.y,
      w: text.length * fontSize * 0.6,
      h: fontSize,
      rotation: 0,
      props: { text, color, fontSize } as TextProps,
    } as any;
    setElements((prev) => [...prev, el]);
    setSelectedId(el.id);
    setShowTextInput(null);
    // Snapshot after adding text/emoji element
    pushHistory();
  };

  const handleUndo = () => {
    if (history.length <= 1) return; // keep at least one state
    const last = history[history.length - 1];
    const prev = history[history.length - 2];
    setRedoStack((r) => [last, ...r]);
    setHistory((h) => h.slice(0, -1));
    restoreState(prev);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const [next, ...rest] = redoStack;
    setRedoStack(rest);
    setHistory((h) => [...h, next]);
    restoreState(next);
  };

  const handleClear = () => {
    const base = baseCanvasRef.current;
    if (!base) return;
    const ctx = base.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (!ctx) return;
    ctx.clearRect(0, 0, base.width, base.height);
    const ink = inkCanvasRef.current;
    const ictx = ink?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (ink && ictx) ictx.clearRect(0, 0, ink.width, ink.height);
    pushHistory();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const base = baseCanvasRef.current!;
      const dpr = window.devicePixelRatio || 1;
      const cw = base.width / dpr;
      const ch = base.height / dpr;
      const scale = Math.min(cw / img.width, ch / img.height, 1);
      const w = img.width * scale;
      const h = img.height * scale;
      const x = (cw - w) / 2;
      const y = (ch - h) / 2;
      const id = Math.random().toString(36).slice(2);
      const el: CanvasElement = { id, type: 'image', x, y, w, h, rotation: 0, props: { src: url, img } } as any;
      setElements((prev) => [...prev, el]);
      setSelectedId(el.id);
      URL.revokeObjectURL(url);
      // Snapshot after adding image element
      pushHistory();
    };
    img.src = url;
  };


  const handleDone = async () => {
    const base = baseCanvasRef.current;
    if (!base) return;
    const dpr = window.devicePixelRatio || 1;
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = base.width;
    exportCanvas.height = base.height;
    const ex = exportCanvas.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
    if (!ex) return;
    ex.drawImage(base, 0, 0);
    ex.save();
    ex.setTransform(dpr, 0, 0, dpr, 0, 0);
    // draw non-highlight elements first
    elements.forEach(el => {
      if (el.type === 'image' && (el.props as any).blend === 'highlight') return;
      drawElement(ex, el);
    });
    // compose and draw highlight union including any in-progress ink
    const union = composeHighlightsUnion(exportCanvas.width, exportCanvas.height, true);
    ex.restore();
    if (union) {
      ex.drawImage(union, 0, 0);
    }
    const bounds = getContentBounds(exportCanvas);
    let dataUrl: string;
    if (bounds) {
      const crop = document.createElement('canvas');
      crop.width = bounds.w;
      crop.height = bounds.h;
      const cx = crop.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
      if (cx) {
        const imgData = ex.getImageData(bounds.x, bounds.y, bounds.w, bounds.h);
        cx.putImageData(imgData, 0, 0);
        dataUrl = crop.toDataURL('image/png');
      } else {
        dataUrl = exportCanvas.toDataURL('image/png');
      }
    } else {
      dataUrl = exportCanvas.toDataURL('image/png');
    }
    const file = await toFileFromDataUrl(dataUrl);
    onDone(dataUrl, file);
  };

  // Toggle brush width on re-select of brushy tools
  const onSelectTool = (next: Tool) => {
    if (next === tool && (tool === 'pencil' || tool === 'highlighter' || tool === 'eraser')) {
      if (next === 'highlighter') {
        setShowWidthSlider(false);
      } else {
        setShowWidthSlider((s) => !s);
      }
    } else {
      setTool(next);
      if (next === 'pencil' || next === 'eraser') setShowWidthSlider(true); else setShowWidthSlider(false);
    }
  };

  const hitTest = (x: number, y: number, list: CanvasElement[]): CanvasElement | null => {
    // iterate reverse for topmost
    for (let i = list.length - 1; i >= 0; i--) {
      const el = list[i];
      if (pointInElement(x, y, el)) return el;
    }
    return null;
  };

  const pointInElement = (px: number, py: number, el: CanvasElement): boolean => {
    // inverse rotate point into element space then test bounding box
    const cx = el.x + el.w / 2; const cy = el.y + el.h / 2;
    const cos = Math.cos(-el.rotation); const sin = Math.sin(-el.rotation);
    const dx = px - cx; const dy = py - cy;
    const rx = cx + dx * cos - dy * sin;
    const ry = cy + dx * sin + dy * cos;
    if (el.type === 'line' || el.type === 'arrow') {
      // distance to segment threshold
      const x1 = el.x; const y1 = el.y; const x2 = el.x + el.w; const y2 = el.y + el.h;
      const dist = pointToSegmentDistance({ x: rx, y: ry }, { x: x1, y: y1 }, { x: x2, y: y2 });
      return dist <= Math.max(6, el.props.strokeWidth + 4);
    }
    return rx >= el.x && rx <= el.x + el.w && ry >= el.y && ry <= el.y + el.h;
  };

  const pointToSegmentDistance = (p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }) => {
    const l2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
    if (l2 === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    const proj = { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) };
    return Math.hypot(p.x - proj.x, p.y - proj.y);
  };

  const hitTestHandle = (x: number, y: number, el: CanvasElement): ResizeHandle | null => {
    const cx = el.x + el.w / 2; const cy = el.y + el.h / 2;
    // Rotation check removed


    const handles: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
    for (const h of handles) {
      const pos = handlePosition(el, h);
      // Increased hit radius for resize handles (8 -> 16 to be very catchy)
      if (Math.abs(x - pos.hx) <= 16 && Math.abs(y - pos.hy) <= 16) return h;
    }
    return null;
  };

  const resizeElement = (el: CanvasElement, handle: ResizeHandle, dx: number, dy: number): CanvasElement => {
    // For simplicity, ignore rotation during resize (approximate in axis-aligned space)
    let { x, y, w, h } = el;
    if (handle.includes('n')) { y += dy; h -= dy; }
    if (handle.includes('s')) { h += dy; }
    if (handle.includes('w')) { x += dx; w -= dx; }
    if (handle.includes('e')) { w += dx; }
    // prevent negative size flip
    const minSize = 5;
    if (w < minSize) { x = x + (w - minSize); w = minSize; }
    if (h < minSize) { y = y + (h - minSize); h = minSize; }
    // If text/emoji, scale font size to match resize (so it "zooms")
    if (el.type === 'text') {
      const prevW = Math.max(1, Math.abs(el.w));
      const prevH = Math.max(1, Math.abs(el.h));
      const wr = Math.max(0.1, Math.abs(w) / prevW);
      const hr = Math.max(0.1, Math.abs(h) / prevH);
      const scale = Math.max(wr, hr);
      const nextFont = Math.max(8, (el.props as TextProps).fontSize * scale);
      const currentProps = el.props as TextProps;
      const text = currentProps.text;
      const color = currentProps.color;
      const newW = text.length * nextFont * 0.6;
      const newH = nextFont;
      return { ...el, x, y, w: newW, h: newH, props: { text, color, fontSize: nextFont } as TextProps } as CanvasElement;
    }
    return { ...el, x, y, w, h } as CanvasElement;
  };

  useEffect(() => {
    if (showTextInput && textInputRef.current) {
      const input = textInputRef.current;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleTextCommit();
        }
        if (e.key === 'Escape') {
          setShowTextInput(null);
        }
      };
      input.addEventListener('keydown', onKey);
      return () => input.removeEventListener('keydown', onKey);
    }
  }, [showTextInput]);

  if (!isOpen) return null;

  return createPortal(
    <div className={`fixed inset-0 ${bgClass} z-[9999] flex flex-col`}>
      <div className={`flex items-center justify-between absolute top-2 right-2 z-[99999999]`}>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDone}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1 border ${panelClass} hover:opacity-90`}
            title="Done"
          >
            <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
            <span>Done</span>
          </button>
          <button
            onClick={onClose}
            className={`px-2 py-1 rounded-full ${panelClass} hover:opacity-90`}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative">
        {/* Hidden file input always mounted for mobile attach */}
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

        <div
          ref={containerRef}
          className="absolute inset-0 bg-[#303030] rounded-b-2xl touch-none"
          onPointerMove={(e) => {
            if (tool !== 'eraser' && tool !== 'pencil' && tool !== 'highlighter') return;
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            drawEraserCursor(x, y);
          }}
          onPointerEnter={(e) => {
            if (tool !== 'eraser' && tool !== 'pencil' && tool !== 'highlighter') return;
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            drawEraserCursor(x, y);
          }}
          onPointerLeave={() => {
            const temp = tempCanvasRef.current;
            const tctx = temp?.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null;
            if (temp && tctx) tctx.clearRect(0, 0, temp.width, temp.height);
          }}
        >
          <canvas
            ref={baseCanvasRef}
            className="absolute inset-0 w-full h-full touch-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={onPointerUp}
            style={{ cursor: (tool === 'eraser' || tool === 'pencil' || tool === 'highlighter') ? 'none' : 'default' }}
          />
          <canvas
            ref={elementsCanvasRef}
            className={`absolute inset-0 w-full h-full touch-none ${tool === 'select' ? 'pointer-events-auto' : 'pointer-events-none'}`}
            style={{ cursor: tool === 'select' ? (transformMode ? 'grabbing' : hoverCursor) : ((tool === 'eraser' || tool === 'pencil' || tool === 'highlighter') ? 'none' : 'default') }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerLeave={onPointerUp}
          />
          {/* Ink (freehand) strokes go above elements while drawing, then get promoted to an element on stroke end. */}
          <canvas
            ref={inkCanvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
          />
          <canvas
            ref={tempCanvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
          />
          {showTextInput && (
            <input
              ref={textInputRef}
              type="text"
              placeholder="Type text and press Enter"
              className={`absolute px-2 py-1 rounded-md text-sm outline-none border ${panelClass}`}
              style={{ left: showTextInput.x, top: showTextInput.y }}
              onBlur={handleTextCommit}
            />
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className={`p-2 md:p-3 overflow-visible bg-transparent p-0 backdrop-blur-sm`}>
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-2 md:gap-3 justify-center overflow-visible">
          {/* Core tools */}
          <div className="hidden sm:flex items-center gap-1 md:gap-2">
            <div className="relative group">
              <ToolButton active={tool === 'select'} onClick={() => onSelectTool('select')} title="Select / Move / Resize / Rotate"><MousePointer2 className="w-4 h-4" /></ToolButton>
            </div>
            <div className="relative group">
              <ToolButton active={tool === 'pencil'} onClick={() => onSelectTool('pencil')} title="Pencil"><PenLine className="w-4 h-4" /></ToolButton>
              {showWidthSlider && tool === 'pencil' && (
                <SliderPopover value={strokeWidth} onChange={setStrokeWidth} />
              )}
            </div>
            <div className="relative group">
              <ToolButton active={tool === 'highlighter'} onClick={() => onSelectTool('highlighter')} title="Highlighter"><Highlighter className="w-4 h-4" /></ToolButton>
              {showWidthSlider && tool === 'highlighter' && (
                <SliderPopover value={strokeWidth} onChange={setStrokeWidth} />
              )}
            </div>
            <div className="relative group">
              <ToolButton active={tool === 'eraser'} onClick={() => onSelectTool('eraser')} title="Eraser"><Eraser className="w-4 h-4" /></ToolButton>
              {showWidthSlider && tool === 'eraser' && (
                <SliderPopover value={strokeWidth} onChange={setStrokeWidth} />
              )}
            </div>
          </div>

          {/* Mobile core tools + overflow */}
          <div className="flex sm:hidden items-center gap-1">
            <div className="relative">
              <ToolButton active={tool === 'select'} onClick={() => onSelectTool('select')} title="Select"><MousePointer2 className="w-4 h-4" /></ToolButton>
            </div>
            <div className="relative">
              <ToolButton active={tool === 'pencil'} onClick={() => onSelectTool('pencil')} title="Pencil"><PenLine className="w-4 h-4" /></ToolButton>
              {showWidthSlider && tool === 'pencil' && (
                <SliderPopover value={strokeWidth} onChange={setStrokeWidth} />
              )}
            </div>
            <div className="relative">
              <ToolButton active={tool === 'highlighter'} onClick={() => onSelectTool('highlighter')} title="Highlighter"><Highlighter className="w-4 h-4" /></ToolButton>
              {showWidthSlider && tool === 'highlighter' && (
                <SliderPopover value={strokeWidth} onChange={setStrokeWidth} />
              )}
            </div>
            <div className="relative">
              <ToolButton active={tool === 'eraser'} onClick={() => onSelectTool('eraser')} title="Eraser"><Eraser className="w-4 h-4" /></ToolButton>
              {showWidthSlider && tool === 'eraser' && (
                <SliderPopover value={strokeWidth} onChange={setStrokeWidth} />
              )}
            </div>
            <ToolButton onClick={handleUndo} title="Undo"><Undo2 className="w-4 h-4" /></ToolButton>
            <ToolButton onClick={handleRedo} title="Redo"><Redo2 className="w-4 h-4" /></ToolButton>
          </div>

          {/* Tools overflow (all devices) */}
          <div className="relative">
            <ToolButton onClick={() => setMobileMenuOpen((s) => !s)} title="More tools">
              <MoreVertical className="w-4 h-4" />
            </ToolButton>
            {mobileMenuOpen && (
              <div className="absolute z-30 bottom-full mb-2 right-1 p-2 rounded-xl border bg-white text-gray-900 border-gray-200 shadow-2xl dark:bg-[#1f1f1f] dark:text-white dark:border-white/10 w-56">
                <div className="flex flex-col">
                  {[
                    { k: 'select', label: 'Select', icon: <MousePointer2 className="w-4 h-4" />, act: () => onSelectTool('select') },
                    { k: 'line', label: 'Line', icon: <Minus className="w-4 h-4" />, act: () => onSelectTool('line') },
                    { k: 'rectangle', label: 'Rectangle', icon: <Square className="w-4 h-4" />, act: () => onSelectTool('rectangle') },
                    { k: 'ellipse', label: 'Ellipse', icon: <Circle className="w-4 h-4" />, act: () => onSelectTool('ellipse') },
                    { k: 'arrow', label: 'Arrow', icon: <ArrowUpRight className="w-4 h-4" />, act: () => onSelectTool('arrow') },
                    { k: 'text', label: 'Text', icon: <Type className="w-4 h-4" />, act: () => onSelectTool('text') },
                    { k: 'image', label: 'Image', icon: <ImageIcon className="w-4 h-4" />, act: () => imageInputRef.current?.click() },
                    { k: 'clear', label: 'Clear', icon: <Trash2 className="w-4 h-4" />, act: () => handleClear() },
                  ].map(item => (
                    <div
                      key={item.k}
                      role="button"
                      tabIndex={0}
                      onClick={() => { setMobileMenuOpen(false); item.act(); }}
                      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setMobileMenuOpen(false); item.act(); } }}
                      className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 cursor-pointer"
                    >
                      {item.icon}
                      <span className="text-sm">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Stroke width */}
          {/* Replaced by SliderPopover above specific brush buttons */}

          {/* Colors */}
          <div className={`relative flex items-center gap-2 px-2 py-1.5 rounded-full border ${panelClass}`}>
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-6 h-6 rounded-full ring-1 ring-black/10 shadow-sm`}
                style={{ backgroundColor: c, outline: color === c ? '2px solid #10b981' : 'none' }}
                aria-label={`Select color ${c}`}
              />
            ))}
            <div className="relative">
              <button
                ref={paletteBtnRef}
                onClick={() => setShowPaletteMore((s) => !s)}
                className={`w-8 h-8 rounded-full border flex items-center justify-center bg-white text-gray-900 border-gray-200 hover:bg-gray-100 dark:bg-[#1f1f1f] dark:text-white dark:border-white/10 dark:hover:bg-white/10`}
                aria-label="More colors"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
              {showPaletteMore && (
                <div
                  ref={palettePopRef}
                  className={`fixed p-2 bottom-12 -translate-x-44 w-[235px] rounded-xl border bg-white text-gray-900 border-gray-200 shadow-lg flex gap-2 flex-col z-[10000] dark:bg-[#1f1f1f] dark:text-white dark:border-white/10`}
                >
                  <div className="px-2 py-1">
                    <HexColorPicker color={color} onChange={setColor} />
                  </div>
                  <div className="relative">


                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Undo/Redo */}
          <div className="flex items-center gap-2 sm:flex hidden">
            <ToolButton onClick={handleUndo} title="Undo"><Undo2 className="w-4 h-4" /></ToolButton>
            <ToolButton onClick={handleRedo} title="Redo"><Redo2 className="w-4 h-4" /></ToolButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Custom slider popover shown above active brush buttons
const SliderPopover: React.FC<{ value: number; onChange: (v: number) => void }>
  = ({ value, onChange }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = useState(false);

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

    const posFromEvent = (e: React.MouseEvent | React.TouchEvent) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return value;
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      return Math.round(1 + ratio * (40 - 1));
    };

    const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      const next = posFromEvent(e);
      onChange(next);
      setDragging(true);
    };
    const moveDrag = (e: React.MouseEvent | React.TouchEvent) => {
      if (!dragging) return;
      e.preventDefault();
      const next = posFromEvent(e);
      onChange(next);
    };
    const endDrag = () => setDragging(false);

    const percent = ((value - 1) / (40 - 1)) * 100;

    return (
      <div className="absolute bottom-full left-1/2 -translate-x-4 mb-2 z-30 select-none">
        <div className="px-3 py-2 rounded-xl border bg-white text-gray-900 border-gray-200 shadow-lg dark:bg-[#1f1f1f] dark:text-white dark:border-white/10 animate-fade-up">
          <div
            ref={trackRef}
            className="relative w-40 h-2 rounded-full bg-gray-200 dark:bg-white/10 cursor-pointer"
            onMouseDown={startDrag}
            onMouseMove={moveDrag}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onTouchStart={startDrag}
            onTouchMove={moveDrag}
            onTouchEnd={endDrag}
          >
            <div className="absolute left-0 top-0 h-2 rounded-full bg-blue-500" style={{ width: `${percent}%` }} />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border border-gray-300 shadow dark:bg-[#2a2a2a] dark:border-white/10"
              style={{ left: `calc(${percent}% - 8px)` }}
            />
          </div>
          <div className="mt-2 text-center text-xs opacity-80">{value}px</div>
        </div>
      </div>
    );
  };

const ToolButton: React.FC<{ active?: boolean; onClick: () => void; title?: string; children: React.ReactNode }>
  = ({ active, onClick, title, children }) => {
    return (
      <button
        onClick={onClick}
        className={`relative group inline-flex items-center justify-center w-9 h-9 md:w-10 md:h-10 p-0 rounded-full border transition-colors 
        ${active
            ? 'bg-[#404040] text-white border-[#404040] hover:bg-[#4a4a4a] dark:bg-[#404040] dark:text-white dark:border-[#404040] dark:hover:bg-[#4a4a4a]'
            : 'bg-white text-gray-900 border-gray-200 hover:bg-gray-100 dark:bg-[#1f1f1f] dark:text-white dark:border-white/10 dark:hover:bg-white/10'
          }
      `}
      >
        {children}
        {title && (
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded-md text-[11px] bg-black/80 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden md:block">
            {title}
          </div>
        )}
      </button>
    );
  };

export default DrawCanvasModal;
